/**
 * WebOs Process Manager
 *
 * Gère le cycle de vie des processus (Workers et Iframes).
 * Maintient la table des processus et orchestre le spawn/kill.
 */

import { createLogger } from '../utils/debug.js';
import { getBasePath, getVfsUrl } from '../utils/paths.js';
import { getFileSystem } from './filesystem.js';

const log = createLogger('Process');

// États possibles d'un processus
export const ProcessState = {
  STARTING: 'starting',
  RUNNING: 'running',
  PAUSED: 'paused',
  TERMINATING: 'terminating',  // En cours de terminaison gracieuse
  TERMINATED: 'terminated'
};

// Types de processus
export const ProcessType = {
  TUI: 'tui',
  GUI: 'gui',
  BACKGROUND: 'background',
  DAEMON: 'daemon'  // Alias pour background (utilisé dans les manifests)
};

/**
 * Structure d'un processus
 * @typedef {Object} Process
 * @property {number} pid - Process ID unique
 * @property {string} name - Nom de l'application
 * @property {string} path - Chemin de l'exécutable
 * @property {ProcessType} type - Type (tui, gui, background)
 * @property {ProcessState} state - État actuel
 * @property {Worker|null} worker - Référence au Worker (si TUI/background)
 * @property {HTMLIFrameElement|null} iframe - Référence à l'iframe (si GUI)
 * @property {MessagePort} kernelPort - Port de communication avec le Kernel
 * @property {MessagePort} processPort - Port côté processus
 * @property {Object} manifest - Manifest de l'application
 * @property {Object} env - Variables d'environnement
 * @property {string[]} args - Arguments de la commande
 * @property {number} parentPid - PID du processus parent
 * @property {number} startTime - Timestamp de démarrage
 * @property {number|null} exitCode - Code de sortie (si terminé)
 */

/**
 * Gestionnaire de processus
 */
export class ProcessManager {
  constructor() {
    /** @type {Map<number, Process>} */
    this.processes = new Map();

    /** @type {number} */
    this.nextPid = 1;

    /** @type {Map<number, Function[]>} */
    this.waitCallbacks = new Map(); // PID -> callbacks attendant la fin

    /** @type {Function|null} */
    this.onProcessMessage = null; // Handler pour les messages IPC

    /** @type {number|null} */
    this.orphanCheckInterval = null;

    /** @type {number|null} */
    this.heartbeatInterval = null;

    log.debug('ProcessManager initialized');

    // Démarrer la surveillance des orphelins et heartbeats
    this.startOrphanCheck();
    this.startHeartbeatCheck();
  }

  /**
   * Démarre la vérification périodique des processus orphelins
   */
  startOrphanCheck() {
    if (this.orphanCheckInterval) return;

    this.orphanCheckInterval = setInterval(() => {
      this.cleanupOrphanProcesses();
    }, 10000); // Toutes les 10 secondes

    log.debug('Orphan process checker started');
  }

  /**
   * Arrête la vérification des orphelins
   */
  stopOrphanCheck() {
    if (this.orphanCheckInterval) {
      clearInterval(this.orphanCheckInterval);
      this.orphanCheckInterval = null;
    }
  }

  /**
   * Démarre la vérification périodique des heartbeats
   */
  startHeartbeatCheck() {
    if (this.heartbeatInterval) return;

    this.heartbeatInterval = setInterval(() => {
      this.checkHeartbeats();
    }, 30000); // Toutes les 30 secondes

    log.debug('Heartbeat checker started');
  }

  /**
   * Arrête la vérification des heartbeats
   */
  stopHeartbeatCheck() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Nettoie les processus orphelins (parents terminés)
   */
  cleanupOrphanProcesses() {
    const orphans = [];

    for (const [pid, process] of this.processes) {
      if (process.state === ProcessState.TERMINATED) continue;

      // Vérifier si le parent existe encore
      if (process.parentPid > 0) {
        const parent = this.getProcess(process.parentPid);
        if (!parent || parent.state === ProcessState.TERMINATED) {
          orphans.push(pid);
        }
      }
    }

    if (orphans.length > 0) {
      log.warn(`Found ${orphans.length} orphan process(es):`, orphans);

      // Ré-attacher les orphelins au processus init (PID 1) ou les terminer
      for (const pid of orphans) {
        const process = this.getProcess(pid);
        if (process) {
          // Option 1: Ré-attacher à init (PID 0 = kernel)
          process.parentPid = 0;
          log.info(`Orphan process ${pid} (${process.name}) re-attached to kernel`);
        }
      }
    }
  }

  /**
   * Vérifie les heartbeats des processus
   */
  checkHeartbeats() {
    const now = Date.now();
    const timeout = 60000; // 60 secondes sans heartbeat = zombie

    for (const [pid, process] of this.processes) {
      if (process.state !== ProcessState.RUNNING) continue;

      // Envoyer un heartbeat
      this.sendHeartbeat(pid);

      // Vérifier si le dernier heartbeat est trop ancien
      if (process.lastHeartbeat && (now - process.lastHeartbeat) > timeout) {
        log.warn(`Process ${pid} (${process.name}) not responding, marking as zombie`);
        // Ne pas terminer automatiquement, juste logger
        // L'utilisateur peut décider de le tuer manuellement
      }
    }
  }

  /**
   * Génère un nouveau PID
   * @returns {number}
   */
  generatePid() {
    return this.nextPid++;
  }

  /**
   * Récupère un processus par son PID
   * @param {number} pid
   * @returns {Process|null}
   */
  getProcess(pid) {
    return this.processes.get(pid) || null;
  }

  /**
   * Liste tous les processus
   * @returns {Process[]}
   */
  listProcesses() {
    return Array.from(this.processes.values());
  }

  /**
   * Liste les processus actifs (non terminés)
   * @returns {Process[]}
   */
  listActiveProcesses() {
    return this.listProcesses().filter(p => p.state !== ProcessState.TERMINATED);
  }

  /**
   * Résout le chemin d'un binaire
   * @param {string} bin - Nom ou chemin du binaire
   * @param {Object} env - Variables d'environnement
   * @returns {Promise<string>} Chemin absolu résolu
   */
  async resolveBinaryPath(bin, env) {
    const fs = getFileSystem();

    // Chemin absolu
    if (bin.startsWith('/')) {
      const resolved = await fs.resolveSymlink(bin).catch(() => bin);
      log.debug('Resolved absolute path:', bin, '->', resolved);
      return resolved;
    }

    // Chemin relatif
    if (bin.startsWith('./') || bin.startsWith('../')) {
      const cwd = env.PWD || '/home/user';
      const fullPath = this.resolvePath(cwd, bin);
      const resolved = await fs.resolveSymlink(fullPath).catch(() => fullPath);
      log.debug('Resolved relative path:', bin, '->', resolved);
      return resolved;
    }

    // Recherche dans PATH
    const pathDirs = (env.PATH || '/bin:/usr/bin').split(':');

    for (const dir of pathDirs) {
      const candidate = `${dir}/${bin}`;

      if (await fs.exists(candidate)) {
        const resolved = await fs.resolveSymlink(candidate).catch(() => candidate);
        log.debug('Resolved from PATH:', bin, '->', resolved);
        return resolved;
      }
    }

    const error = new Error(`Command not found: ${bin}`);
    error.code = 'ENOENT';
    throw error;
  }

  /**
   * Résout un chemin relatif
   * @param {string} base
   * @param {string} relative
   * @returns {string}
   */
  resolvePath(base, relative) {
    const parts = base.split('/').filter(p => p);
    const relParts = relative.split('/');

    for (const part of relParts) {
      if (part === '..') {
        parts.pop();
      } else if (part !== '.' && part !== '') {
        parts.push(part);
      }
    }

    return '/' + parts.join('/');
  }

  /**
   * Obtient le répertoire contenant un fichier
   * @param {string} path
   * @returns {string}
   */
  getDirectory(path) {
    const lastSlash = path.lastIndexOf('/');
    return lastSlash <= 0 ? '/' : path.slice(0, lastSlash);
  }

  /**
   * Lance un nouveau processus
   * @param {string} bin - Chemin ou nom du binaire
   * @param {string[]} args - Arguments
   * @param {Object} options - Options (env, cwd, parentPid)
   * @returns {Promise<Process>}
   */
  async spawn(bin, args = [], options = {}) {
    const timer = log.time(`spawn ${bin}`);
    const pid = this.generatePid();

    const env = {
      HOME: '/home/user',
      USER: 'user',
      PATH: '/bin:/usr/bin:/home/user/.local/bin',
      PWD: options.cwd || '/home/user',
      SHELL: '/bin/sh',
      TERM: 'xterm-256color',
      ...options.env
    };

    log.info(`Spawning process: ${bin} (PID: ${pid})`);
    log.debug('Args:', args);
    log.debug('Env:', env);

    try {
      // Résoudre le chemin du binaire
      const resolvedPath = await this.resolveBinaryPath(bin, env);
      log.debug('Resolved path:', resolvedPath);

      // Charger le manifest
      const manifest = await this.loadManifest(resolvedPath);
      log.debug('Manifest loaded:', manifest.name, manifest.type);

      // Calculer le chemin d'entree (pour les app bundles, c'est path + entry)
      const entryPath = await this.computeEntryPath(resolvedPath, manifest);
      log.debug('Entry path:', entryPath);

      // Créer le canal de communication
      const channel = new MessageChannel();
      const kernelPort = channel.port1;
      const processPort = channel.port2;

      // Créer le processus
      const process = {
        pid,
        name: manifest.name || bin,
        path: resolvedPath,
        entryPath,  // Chemin complet vers le fichier JS d'entree
        type: manifest.type || ProcessType.TUI,
        state: ProcessState.STARTING,
        worker: null,
        iframe: null,
        kernelPort,
        processPort,
        manifest,
        env,
        args,
        parentPid: options.parentPid || 0,
        startTime: Date.now(),
        exitCode: null
      };

      this.processes.set(pid, process);

      // Configurer le handler de messages
      kernelPort.onmessage = (event) => {
        this.handleProcessMessage(pid, event.data);
      };

      // Lancer selon le type
      if (process.type === ProcessType.GUI) {
        await this.spawnGUI(process);
      } else {
        await this.spawnWorker(process);
      }

      timer.end();
      return process;

    } catch (error) {
      timer.end();
      log.error(`Failed to spawn ${bin}:`, error.message);
      throw error;
    }
  }

  /**
   * Charge le manifest d'une application
   * @param {string} execPath - Chemin de l'exécutable (peut etre un fichier ou un repertoire)
   * @returns {Promise<Object>}
   */
  async loadManifest(execPath) {
    const fs = getFileSystem();

    // Determiner si execPath est un repertoire (app bundle) ou un fichier
    let manifestPath;
    try {
      const stat = await fs.stat(execPath);
      if (stat.type === 'directory') {
        // App bundle: manifest dans le repertoire
        manifestPath = `${execPath}/manifest.json`;
      } else {
        // Fichier script: manifest dans le parent
        const dir = this.getDirectory(execPath);
        manifestPath = `${dir}/manifest.json`;
      }
    } catch {
      // Si stat echoue, essayer avec le parent
      const dir = this.getDirectory(execPath);
      manifestPath = `${dir}/manifest.json`;
    }

    try {
      const content = await fs.readFile(manifestPath);
      const manifest = JSON.parse(content);

      log.debug('Loaded manifest from:', manifestPath);
      return manifest;

    } catch (error) {
      log.warn(`No manifest found at ${manifestPath}, using defaults`);

      // Manifest par défaut
      return {
        name: execPath.split('/').pop().replace('.js', ''),
        type: 'tui',
        entry: execPath.split('/').pop(),
        permissions: ['fs.read', 'fs.write']
      };
    }
  }

  /**
   * Calcule le chemin complet vers le fichier JS d'entree
   * Pour les app bundles (repertoires), c'est path + '/' + manifest.entry
   * Pour les fichiers JS, c'est le chemin lui-meme
   * @param {string} resolvedPath - Chemin resolu
   * @param {Object} manifest - Manifest de l'app
   * @returns {Promise<string>}
   */
  async computeEntryPath(resolvedPath, manifest) {
    const fs = getFileSystem();

    try {
      const stat = await fs.stat(resolvedPath);
      if (stat.type === 'directory') {
        // App bundle: path + entry
        const entry = manifest.entry || `${manifest.name}.js`;
        return `${resolvedPath}/${entry}`;
      }
    } catch {
      // Si stat echoue, supposer que c'est un fichier
    }

    // Fichier direct
    return resolvedPath;
  }

  /**
   * Lance un processus Worker (TUI ou Background/Daemon)
   * @param {Process} process
   * @returns {Promise<void>}
   */
  async spawnWorker(process) {
    // Déterminer le bootstrap à utiliser
    const isTui = process.type === ProcessType.TUI;
    const isDaemon = process.type === ProcessType.BACKGROUND || process.type === ProcessType.DAEMON;

    let workerUrl;
    let bootstrapType = '';

    if (isTui) {
      workerUrl = getVfsUrl('/usr/lib/shell/tui-worker-bootstrap.js');
      bootstrapType = '(TUI bootstrap)';
    } else if (isDaemon) {
      workerUrl = getVfsUrl('/usr/lib/shell/daemon-worker-bootstrap.js');
      bootstrapType = '(Daemon bootstrap)';
    } else {
      // Fallback: charger directement l'app (legacy)
      workerUrl = getVfsUrl(process.path);
    }

    log.debug('Creating Worker:', workerUrl, bootstrapType);

    try {
      const worker = new Worker(workerUrl, { type: 'module' });
      process.worker = worker;

      // Gérer les erreurs du Worker
      worker.onerror = (error) => {
        log.error(`Worker error (PID ${process.pid}):`, error.message);
        this.terminateProcess(process.pid, 1);
      };

      // Le handshake contient les infos du processus
      const handshakePayload = {
        pid: process.pid,
        manifest: process.manifest,
        env: process.env,
        args: process.args
      };

      // Ajouter le chemin de l'app pour les bootstraps (TUI et Daemon)
      if (isTui || isDaemon) {
        handshakePayload.appPath = getVfsUrl(process.entryPath);
      }

      // Envoyer le handshake
      const handshake = {
        type: 'SYS_HANDSHAKE',
        id: crypto.randomUUID(),
        from: 0,
        to: process.pid,
        timestamp: Date.now(),
        payload: handshakePayload
      };

      log.debug('Sending handshake to PID', process.pid);

      worker.postMessage(handshake, [process.processPort]);

      // Attendre l'ACK avec timeout
      await this.waitForHandshakeAck(process, 5000);

      process.state = ProcessState.RUNNING;
      log.info(`Process started: ${process.name} (PID: ${process.pid})`);

    } catch (error) {
      log.error('Worker creation failed:', error.message);
      this.processes.delete(process.pid);
      throw error;
    }
  }

  /**
   * Lance un processus GUI (Iframe)
   * @param {Process} process
   * @returns {Promise<void>}
   */
  async spawnGUI(process) {
    log.debug('Creating GUI iframe for:', process.name);

    // Générer le wrapper HTML
    const html = this.createGUIWrapper(process);
    const blob = new Blob([html], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);

    const iframe = document.createElement('iframe');
    iframe.src = blobUrl;
    iframe.style.display = 'none'; // Sera affiché par le Shell/WindowManager
    iframe.dataset.pid = process.pid;

    process.iframe = iframe;

    // Configurer le handler de messages sur kernelPort AVANT d'attendre
    process.kernelPort.onmessage = (event) => {
      this.handleProcessMessage(process.pid, event.data);
    };

    // Attendre le message GUI_READY et envoyer le handshake
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('GUI initialization timeout'));
      }, 10000);

      const handler = (event) => {
        if (event.data?.type === 'GUI_READY' && event.source === iframe.contentWindow) {
          clearTimeout(timeout);
          window.removeEventListener('message', handler);

          // Envoyer le handshake avec appPath pour le bootstrap
          iframe.contentWindow.postMessage({
            type: 'SYS_HANDSHAKE',
            id: crypto.randomUUID(),
            from: 0,
            to: process.pid,
            timestamp: Date.now(),
            payload: {
              pid: process.pid,
              manifest: process.manifest,
              env: process.env,
              args: process.args,
              appPath: getVfsUrl(process.entryPath)
            }
          }, '*', [process.processPort]);

          resolve();
        }
      };

      window.addEventListener('message', handler);
      document.body.appendChild(iframe);
    });

    // Attendre l'ACK du handshake avant de retourner
    // Cela garantit que le bootstrap a bien reçu et traité le handshake
    // avant que le shell puisse manipuler l'iframe
    await this.waitForHandshakeAck(process, 5000);

    process.state = ProcessState.RUNNING;
    log.info(`GUI process started: ${process.name} (PID: ${process.pid})`);
  }

  /**
   * Crée le wrapper HTML pour une application GUI
   * Le wrapper charge le bootstrap GUI qui s'occupera de charger l'app
   * @param {Process} process
   * @returns {string}
   */
  createGUIWrapper(process) {
    // Utiliser des URLs absolues complètes pour que l'iframe blob puisse résoudre les imports
    const origin = window.location.origin;
    const basePath = getBasePath();
    const vfsBase = origin + basePath + 'fs';

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${process.manifest.displayName || process.name}</title>
  <script type="importmap">
  {
    "imports": {
      "libwebos": "${vfsBase}/usr/lib/libwebos/index.js",
      "libwebos/": "${vfsBase}/usr/lib/libwebos/",
      "webos-sdk": "${vfsBase}/usr/lib/webos-sdk/index.js",
      "webos-sdk/": "${vfsBase}/usr/lib/webos-sdk/",
      "webos-sdk/gui": "${vfsBase}/usr/lib/webos-sdk/gui/index.js"
    }
  }
  </script>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width: 100%; height: 100%; overflow: hidden; }
  </style>
</head>
<body>
  <script type="module" src="${vfsBase}/usr/lib/shell/gui-bootstrap.js"></script>
</body>
</html>`;
  }

  /**
   * Attend l'ACK du handshake
   * @param {Process} process
   * @param {number} timeout
   * @returns {Promise<void>}
   */
  waitForHandshakeAck(process, timeout) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`Handshake timeout for PID ${process.pid}`));
      }, timeout);

      const originalHandler = process.kernelPort.onmessage;

      process.kernelPort.onmessage = (event) => {
        if (event.data?.type === 'SYS_HANDSHAKE_ACK') {
          clearTimeout(timer);
          process.kernelPort.onmessage = originalHandler;
          log.debug('Handshake ACK received from PID', process.pid);
          resolve();
        } else {
          // Passer les autres messages au handler original
          if (originalHandler) originalHandler(event);
        }
      };
    });
  }

  /**
   * Gère un message venant d'un processus
   * @param {number} pid
   * @param {Object} message
   */
  handleProcessMessage(pid, message) {
    log.debug(`Message from PID ${pid}:`, message.type);

    // Message de sortie
    if (message.type === 'SYS_EXIT') {
      this.terminateProcess(pid, message.payload?.code || 0);
      return;
    }

    // Heartbeat ACK
    if (message.type === 'SYS_HEARTBEAT_ACK') {
      const process = this.getProcess(pid);
      if (process) {
        process.lastHeartbeat = Date.now();
      }
      return;
    }

    // Déléguer au handler IPC
    if (this.onProcessMessage) {
      this.onProcessMessage(pid, message);
    }
  }

  /**
   * Envoie un message à un processus
   * @param {number} pid
   * @param {Object} message
   * @param {Transferable[]} transferables
   */
  sendToProcess(pid, message, transferables = []) {
    const process = this.getProcess(pid);

    if (!process || process.state === ProcessState.TERMINATED) {
      log.warn(`Cannot send to PID ${pid}: process not found or terminated`);
      return false;
    }

    try {
      process.kernelPort.postMessage(message, transferables);
      log.debug(`Message sent to PID ${pid}:`, message.type);
      return true;
    } catch (error) {
      log.error(`Failed to send to PID ${pid}:`, error.message);
      return false;
    }
  }

  /**
   * Termine un processus
   * @param {number} pid
   * @param {number} exitCode
   * @param {boolean} force - Si true, terminaison forcée (SIGKILL)
   */
  terminateProcess(pid, exitCode = 0, force = false) {
    const process = this.getProcess(pid);

    if (!process) {
      log.warn(`Cannot terminate PID ${pid}: not found`);
      return;
    }

    // Éviter les appels multiples pendant la terminaison gracieuse
    if (process.state === ProcessState.TERMINATED) {
      log.debug(`PID ${pid} already terminated`);
      return;
    }

    if (process.state === ProcessState.TERMINATING && !force) {
      log.debug(`PID ${pid} already terminating, ignoring duplicate call`);
      return;
    }

    log.info(`Terminating PID ${pid} (code: ${exitCode}, force: ${force})`);

    // Envoyer SIGTERM si pas forcé
    if (!force && process.state === ProcessState.RUNNING) {
      // Marquer comme en cours de terminaison AVANT d'envoyer le signal
      process.state = ProcessState.TERMINATING;

      this.sendToProcess(pid, {
        type: 'SYS_KILL',
        id: crypto.randomUUID(),
        from: 0,
        to: pid,
        timestamp: Date.now(),
        payload: { signal: 'SIGTERM' }
      });

      // Donner 2 secondes pour se terminer proprement
      setTimeout(() => {
        if (this.getProcess(pid)?.state === ProcessState.TERMINATING) {
          this.terminateProcess(pid, exitCode, true);
        }
      }, 2000);

      return;
    }

    // Terminaison effective
    process.state = ProcessState.TERMINATED;
    process.exitCode = exitCode;
    process.terminatedAt = Date.now();

    // Nettoyer les ressources
    this.cleanupProcess(pid);

    // Notifier les processus en attente
    const callbacks = this.waitCallbacks.get(pid) || [];
    for (const cb of callbacks) {
      cb(exitCode);
    }
    this.waitCallbacks.delete(pid);

    // Terminer les processus enfants (cascade)
    for (const [childPid, childProcess] of this.processes) {
      if (childProcess.parentPid === pid && childProcess.state !== ProcessState.TERMINATED) {
        log.debug(`Terminating child process ${childPid} of parent ${pid}`);
        this.terminateProcess(childPid, exitCode, true);
      }
    }

    log.info(`Process terminated: ${process.name} (PID: ${pid}, code: ${exitCode})`);
  }

  /**
   * Attend la fin d'un processus
   * @param {number} pid
   * @returns {Promise<number>} Code de sortie
   */
  waitForProcess(pid) {
    const process = this.getProcess(pid);

    if (!process) {
      return Promise.reject(new Error(`Process ${pid} not found`));
    }

    if (process.state === ProcessState.TERMINATED) {
      return Promise.resolve(process.exitCode);
    }

    return new Promise((resolve) => {
      if (!this.waitCallbacks.has(pid)) {
        this.waitCallbacks.set(pid, []);
      }
      this.waitCallbacks.get(pid).push(resolve);
    });
  }

  /**
   * Envoie un heartbeat à un processus
   * @param {number} pid
   */
  sendHeartbeat(pid) {
    const process = this.getProcess(pid);
    if (!process || process.state !== ProcessState.RUNNING) return;

    this.sendToProcess(pid, {
      type: 'SYS_HEARTBEAT',
      id: crypto.randomUUID(),
      from: 0,
      to: pid,
      timestamp: Date.now(),
      payload: { seq: Date.now() }
    });
  }

  /**
   * Met en pause un processus (SIGSTOP)
   * @param {number} pid
   * @returns {boolean} Succès
   */
  pauseProcess(pid) {
    const process = this.getProcess(pid);

    if (!process) {
      log.warn(`Cannot pause PID ${pid}: not found`);
      return false;
    }

    if (process.state !== ProcessState.RUNNING) {
      log.warn(`Cannot pause PID ${pid}: not running (state: ${process.state})`);
      return false;
    }

    log.info(`Pausing process ${pid} (${process.name})`);

    // Envoyer le signal SIGSTOP
    this.sendToProcess(pid, {
      type: 'SYS_PAUSE',
      id: crypto.randomUUID(),
      from: 0,
      to: pid,
      timestamp: Date.now(),
      payload: { signal: 'SIGSTOP' }
    });

    process.state = ProcessState.PAUSED;
    process.pausedAt = Date.now();

    return true;
  }

  /**
   * Reprend un processus en pause (SIGCONT)
   * @param {number} pid
   * @returns {boolean} Succès
   */
  resumeProcess(pid) {
    const process = this.getProcess(pid);

    if (!process) {
      log.warn(`Cannot resume PID ${pid}: not found`);
      return false;
    }

    if (process.state !== ProcessState.PAUSED) {
      log.warn(`Cannot resume PID ${pid}: not paused (state: ${process.state})`);
      return false;
    }

    log.info(`Resuming process ${pid} (${process.name})`);

    // Envoyer le signal SIGCONT
    this.sendToProcess(pid, {
      type: 'SYS_RESUME',
      id: crypto.randomUUID(),
      from: 0,
      to: pid,
      timestamp: Date.now(),
      payload: { signal: 'SIGCONT' }
    });

    process.state = ProcessState.RUNNING;
    delete process.pausedAt;

    return true;
  }

  /**
   * Envoie un signal à un processus
   * @param {number} pid
   * @param {string} signal - SIGTERM, SIGKILL, SIGSTOP, SIGCONT
   * @returns {boolean} Succès
   */
  sendSignal(pid, signal) {
    switch (signal) {
      case 'SIGTERM':
        this.terminateProcess(pid, 0, false);
        return true;
      case 'SIGKILL':
        this.terminateProcess(pid, 0, true);
        return true;
      case 'SIGSTOP':
        return this.pauseProcess(pid);
      case 'SIGCONT':
        return this.resumeProcess(pid);
      default:
        log.warn(`Unknown signal: ${signal}`);
        return false;
    }
  }

  /**
   * Nettoie les ressources d'un processus terminé
   * @param {number} pid
   */
  cleanupProcess(pid) {
    const process = this.getProcess(pid);
    if (!process) return;

    log.debug(`Cleaning up resources for PID ${pid}`);

    // Fermer le port de communication
    try {
      if (process.kernelPort) {
        process.kernelPort.close();
        process.kernelPort = null;
      }
    } catch (e) {
      log.debug(`Failed to close kernelPort for PID ${pid}:`, e.message);
    }

    // Terminer le Worker
    if (process.worker) {
      try {
        process.worker.terminate();
      } catch (e) {
        log.debug(`Failed to terminate worker for PID ${pid}:`, e.message);
      }
      process.worker = null;
    }

    // Supprimer l'iframe
    if (process.iframe) {
      try {
        if (process.iframe.src) {
          URL.revokeObjectURL(process.iframe.src);
        }
        process.iframe.remove();
      } catch (e) {
        log.debug(`Failed to remove iframe for PID ${pid}:`, e.message);
      }
      process.iframe = null;
    }

    // Libérer les références
    process.manifest = null;
    process.env = null;
    process.args = null;
  }

  /**
   * Supprime les processus terminés de la mémoire
   * @param {number} maxAge - Âge maximum en ms (défaut: 5 minutes)
   */
  purgeTerminatedProcesses(maxAge = 300000) {
    const now = Date.now();
    const toPurge = [];

    for (const [pid, process] of this.processes) {
      if (process.state === ProcessState.TERMINATED) {
        const age = now - (process.terminatedAt || process.startTime);
        if (age > maxAge) {
          toPurge.push(pid);
        }
      }
    }

    for (const pid of toPurge) {
      this.cleanupProcess(pid);
      this.processes.delete(pid);
      log.debug(`Purged terminated process PID ${pid}`);
    }

    if (toPurge.length > 0) {
      log.info(`Purged ${toPurge.length} terminated process(es)`);
    }
  }

  /**
   * Obtient les statistiques du gestionnaire de processus
   * @returns {Object}
   */
  getStats() {
    let running = 0, paused = 0, terminated = 0;

    for (const process of this.processes.values()) {
      switch (process.state) {
        case ProcessState.RUNNING:
        case ProcessState.STARTING:
          running++;
          break;
        case ProcessState.PAUSED:
          paused++;
          break;
        case ProcessState.TERMINATED:
          terminated++;
          break;
      }
    }

    return {
      total: this.processes.size,
      running,
      paused,
      terminated,
      nextPid: this.nextPid
    };
  }

  /**
   * Termine tous les processus
   */
  terminateAll() {
    log.warn('Terminating all processes...');

    // D'abord, arrêter les vérifications périodiques
    this.stopOrphanCheck();
    this.stopHeartbeatCheck();

    for (const [pid] of this.processes) {
      this.terminateProcess(pid, 0, true);
    }

    // Purger immédiatement
    this.purgeTerminatedProcesses(0);
  }

  /**
   * Arrête le gestionnaire proprement
   */
  shutdown() {
    log.info('ProcessManager shutting down...');
    this.terminateAll();
    this.processes.clear();
    this.waitCallbacks.clear();
  }
}

// Instance singleton
let instance = null;

/**
 * Obtient l'instance du ProcessManager
 * @returns {ProcessManager}
 */
export function getProcessManager() {
  if (!instance) {
    instance = new ProcessManager();
  }
  return instance;
}
